/* Store Platform showroom presence, frontend contract V1 / ICR-004.
   Product identity is always the canonical Shopify Product GID. The public platform owns productMatch; the theme only
   translates that result to presentation. custom.winkel is deliberately not read here and is never a customer-facing
   fallback. One GET /public/v1/stores?productGid=&brand= is cached per context for the page lifetime. */
(function () {
  'use strict';
  if (window.__bbShowroomEvidenceInitialized) return;
  window.__bbShowroomEvidenceInitialized = true;
  const BB = window.BB;
  const LEVELS = ['exact_product', 'brand_only', 'no_specific_match'];
  const POSITIVE_EXACT_STATUSES = ['in_assortment', 'on_display', 'available_for_demo'];
  const GID_PATTERN = /^gid:\/\/shopify\/Product\/[0-9]{1,20}$/;
  const RANK = { exact_product: 0, brand_only: 1, no_specific_match: 2 };
  const cache = {};
  function isGid(value) { return typeof value === 'string' && GID_PATTERN.test(value); }
  function levelOf(store) {
    const pm = store && typeof store === 'object' ? store.productMatch : null;
    if (!pm || typeof pm !== 'object') return null;
    return LEVELS.indexOf(pm.level) === -1 ? null : pm.level;
  }
  function normalize(store) {
    const level = levelOf(store);
    const pm = level ? store.productMatch : null;
    const brand = pm && pm.brand && typeof pm.brand === 'object' && typeof pm.brand.name === 'string'
      ? { slug: typeof pm.brand.slug === 'string' ? pm.brand.slug : '', name: pm.brand.name } : null;
    const status = level === 'exact_product' && pm.product && typeof pm.product === 'object' && typeof pm.product.status === 'string'
      ? pm.product.status : null;
    return {
      slug: store && typeof store.slug === 'string' ? store.slug : null,
      name: store && typeof store.name === 'string' ? store.name : null,
      level: level || 'unknown',
      positive: level === 'brand_only' || (level === 'exact_product' && POSITIVE_EXACT_STATUSES.indexOf(status) !== -1),
      brand: brand,
      status: status
    };
  }
  function isPositiveExactStatus(status) { return POSITIVE_EXACT_STATUSES.indexOf(status) !== -1; }
  function selectStores(stores, exactOnly) {
    const exact = [];
    const brand = [];
    (stores || []).forEach((store) => {
      const row = normalize(store);
      if (!row.slug) return;
      if (row.level === 'exact_product' && isPositiveExactStatus(row.status)) exact.push(store);
      else if (row.level === 'brand_only') brand.push(store);
    });
    const selected = exact.length ? exact : (exactOnly ? [] : brand);
    return { exact, brand, selected, mode: exact.length ? 'exact' : (selected.length ? 'brand' : 'none') };
  }
  function activateBooking(link, store) {
    if (!link || !store || !store.slug) return;
    if (link.hasAttribute('data-bb-appointment-pending')) {
      const readyHref = link.getAttribute('data-bb-booking-ready-url');
      const readyLabel = link.getAttribute('data-bb-booking-ready-label');
      if (readyHref) link.setAttribute('href', readyHref);
      if (readyLabel) link.textContent = readyLabel;
      link.removeAttribute('data-bb-appointment-pending');
      link.setAttribute('data-bb-appointment', '');
      link.setAttribute('data-bb-track', 'appointment_click');
    }
    link.setAttribute('data-store-slug', store.slug);
    if (store.name) link.setAttribute('data-store-name', store.name);
  }
  function deferBooking(link, fallbackHref, fallbackLabel) {
    if (!link || link.hasAttribute('data-bb-appointment-pending')) return;
    if (!link.hasAttribute('data-bb-appointment')) return;
    link.setAttribute('data-bb-booking-ready-url', link.getAttribute('href') || '');
    link.setAttribute('data-bb-booking-ready-label', link.textContent.trim());
    link.removeAttribute('data-bb-appointment');
    link.setAttribute('data-bb-appointment-pending', '');
    link.setAttribute('data-bb-track', 'contact_click');
    if (fallbackHref) link.setAttribute('href', fallbackHref);
    if (fallbackLabel) link.textContent = fallbackLabel;
  }
  function rank(level) { return Object.prototype.hasOwnProperty.call(RANK, level) ? RANK[level] : 3; }
  function context(input) {
    const src = input || {};
    const out = {};
    if (isGid(src.productGid)) out.productGid = src.productGid;
    const brand = typeof src.brand === 'string' ? src.brand.trim() : '';
    if (brand && brand.length <= 120) out.brand = brand;
    return out;
  }
  function contextFromPage() {
    const pc = BB.pageContext ? BB.pageContext() : {};
    return context({ productGid: pc.product_gid, brand: pc.brand || pc.vendor });
  }
  function fetchStores(ctx) {
    const c = context(ctx);
    const q = [];
    if (c.productGid) q.push('productGid=' + encodeURIComponent(c.productGid));
    if (c.brand) q.push('brand=' + encodeURIComponent(c.brand));
    const url = BB.platformUrl('/public/v1/stores' + (q.length ? '?' + q.join('&') : ''));
    if (!url) return Promise.reject(new Error('platform-not-configured'));
    if (!cache[url]) {
      cache[url] = BB.fetchJSON(url).then((data) => {
        if (!data || !Array.isArray(data.stores)) throw new Error('malformed-response');
        return data.stores;
      });
      cache[url].catch(() => { delete cache[url]; });
    }
    return cache[url];
  }
  function fetchEvidence(ctx) { return fetchStores(ctx).then((stores) => stores.map(normalize)); }
  function names(list) { return list.map((row) => row.name).filter(Boolean).join(', '); }
  function presence(stores) {
    const rows = (stores || []).map(normalize);
    const exact = rows.filter((row) => row.level === 'exact_product' && isPositiveExactStatus(row.status));
    const brand = rows.filter((row) => row.level === 'brand_only');
    const selected = exact.length ? exact : brand;
    if (!selected.length) return { level: 'none', rows: [], text: '' };
    if (!exact.length) {
      return { level: 'brand_only', rows: selected, text: BB.t(selected.length === 1 ? 'presenceBrandOne' : 'presenceBrandMany', { stores: names(selected) }) };
    }
    const groups = [
      { status: 'available_for_demo', one: 'presenceDemoOne', many: 'presenceDemoMany' },
      { status: 'on_display', one: 'presenceDisplayOne', many: 'presenceDisplayMany' },
      { status: 'in_assortment', one: 'presenceAssortmentOne', many: 'presenceAssortmentMany' }
    ];
    const parts = [];
    groups.forEach((group) => {
      const subset = exact.filter((row) => row.status === group.status);
      if (subset.length) parts.push(BB.t(subset.length === 1 ? group.one : group.many, { stores: names(subset) }));
    });
    return { level: 'exact_product', rows: exact, text: parts.join(' ') };
  }
  function markEvidence(el, stores) {
    const map = {};
    (stores || []).map(normalize).forEach((row) => { if (row.slug) map[row.slug] = { level: row.level, status: row.status }; });
    el.setAttribute('data-bb-evidence', JSON.stringify(map));
    el.setAttribute('data-bb-evidence-state', 'loaded');
  }
  function initReadiness(root) {
    (root || document).querySelectorAll('[data-bb-showroom-evidence]:not([data-bb-evidence-state])').forEach((el) => {
      const ctx = contextFromPage();
      if (!ctx.productGid || !BB.platformUrl('/')) { el.setAttribute('data-bb-evidence-state', 'none'); return; }
      el.setAttribute('data-bb-evidence-state', 'loading');
      fetchStores(ctx).then((stores) => markEvidence(el, stores)).catch(() => el.setAttribute('data-bb-evidence-state', 'error'));
    });
  }
  function initPdpRouting() {
    const pc = BB.pageContext ? BB.pageContext() : {};
    const hero = document.querySelector('.bb-product[data-product-id][data-product-mode="showroom"]');
    if (!hero || pc.source_surface !== 'pdp') return;
    if (hero.hasAttribute('data-bb-outlet-sold')) return;
    hero.setAttribute('data-bb-pdp-route-bound', '');
    const ctx = contextFromPage();
    if (!ctx.productGid) return;
    const unique = hero.hasAttribute('data-bb-unique-showroom');
    const main = document.getElementById('MainContent');
    if (!main) return;
    if (unique) {
      const reference = main.querySelector('[data-bb-appointment-pending]');
      const fallbackHref = reference && reference.getAttribute('href');
      const fallbackLabel = reference && reference.textContent.trim();
      const readyHref = reference && reference.getAttribute('data-bb-booking-ready-url');
      document.querySelectorAll('[data-bb-appointment]').forEach((link) => {
        if (link.hasAttribute('data-bb-product-route')) return;
        if (link.hasAttribute('data-product-gid') && link.getAttribute('data-product-gid') !== ctx.productGid) return;
        deferBooking(link, fallbackHref, fallbackLabel);
        if (readyHref && link.hasAttribute('data-bb-appointment-pending')) {
          link.setAttribute('data-bb-booking-ready-url', readyHref);
        }
      });
    }
    fetchStores(ctx).then((stores) => {
      const selection = selectStores(stores, true);
      const selected = selection.exact.length === 1 ? selection.exact[0] : null;
      const scope = unique ? document : main;
      scope.querySelectorAll('[data-bb-appointment], [data-bb-appointment-pending]').forEach((link) => {
        if (link.hasAttribute('data-product-gid') && link.getAttribute('data-product-gid') !== ctx.productGid) return;
        const card = link.closest('.bb-store-card');
        if (unique) {
          if (!selected || (card && card.getAttribute('data-store-slug') !== selected.slug)) return;
          activateBooking(link, selected);
          link.setAttribute('data-bb-product-route', '');
        } else if (selected && !card && !link.hasAttribute('data-store-slug')) {
          activateBooking(link, selected);
          link.setAttribute('data-bb-product-route', '');
        }
      });
      hero.setAttribute('data-bb-store-route-state', selected ? 'single-exact' : (selection.exact.length > 1 ? 'multiple-exact' : 'unconfirmed'));
    }).catch(() => {
      hero.setAttribute('data-bb-store-route-state', 'unavailable');
    });
  }
  function initPresence(root) {
    (root || document).querySelectorAll('[data-bb-product-stores]:not([data-bb-presence-bound])').forEach((el) => {
      el.setAttribute('data-bb-presence-bound', '');
      const ctx = context({ productGid: el.getAttribute('data-product-gid') || '', brand: el.getAttribute('data-brand') || '' });
      const text = el.querySelector('[data-bb-product-stores-text]');
      if (!ctx.productGid || !BB.platformUrl('/')) {
        el.setAttribute('data-bb-presence-state', 'none');
        if (text) text.hidden = true;
        return;
      }
      el.setAttribute('data-bb-presence-state', 'loading');
      fetchStores(ctx).then((stores) => {
        const result = presence(stores);
        if (text) { text.textContent = result.text; text.hidden = !result.text; }
        el.setAttribute('data-bb-presence-level', result.level);
        el.setAttribute('data-bb-presence-state', 'loaded');
      }).catch(() => {
        if (text) text.hidden = true;
        el.setAttribute('data-bb-presence-state', 'error');
      });
    });
  }
  BB.showroom = {
    LEVELS, POSITIVE_EXACT_STATUSES, isGid, levelOf, normalize, isPositiveExactStatus, rank, context, contextFromPage,
    fetchStores, fetchEvidence, presence, selectStores, activateBooking, deferBooking, initReadiness, initPresence
  };
  function init(root) { initReadiness(root); initPresence(root); initPdpRouting(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document)); else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();